import React from 'react';
import { useTranslation } from 'react-i18next';
import { Home } from 'lucide-react';
import { Link } from 'react-router-dom';

type ErrorBoundaryState = {
  hasError: boolean;
};

class ErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught an error:', error);
    }
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}

const ErrorFallback: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#f7f2ed] flex items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-[#4B2E2E]/20">500</h1>
        <h2 className="text-2xl font-bold text-[#4B2E2E] mt-4">
          {t('errorBoundary.title', 'Něco se pokazilo')}
        </h2>
        <p className="text-gray-600 mt-3 max-w-md mx-auto">
          {t('errorBoundary.description', 'Zkuste stránku obnovit, nebo se vraťte na hlavní stránku.')}
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#4B2E2E] text-white px-6 py-3 rounded-xl font-semibold hover:brightness-110 transition"
          >
            <Home className="w-5 h-5" />
            {t('errorBoundary.home', 'Zpět na hlavní stránku')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;
